import api from './client'
import { toNum, unwrap, type ApiEnvelope } from './global'

/**
 * `/api/tasks/stopped` — `tellStopped` wire shape (see
 * `internal/api/jsonrpc.go: buildStatusResponse`). All numeric fields arrive as
 * decimal strings; `status` is one of complete|error|removed|paused.
 */
export interface RawStoppedTask {
  gid: string
  status: string
  totalLength: string
  completedLength: string
  uploadLength?: string
  downloadSpeed?: string
  errorMessage?: string
  dir?: string
  files?: Array<{ path?: string; length?: string }>
}

export interface CompletedTask {
  gid: string
  /** basename of the output path, falling back to the gid */
  name: string
  /** full output path when known */
  path: string
  status: string
  /** bytes */
  totalLength: number
  /** bytes */
  completedLength: number
  errorMessage?: string
}

function basename(p: string): string {
  if (!p) return ''
  const parts = p.split(/[\\/]/).filter(Boolean)
  return parts.length > 0 ? parts[parts.length - 1] : p
}

function normalize(raw: RawStoppedTask): CompletedTask {
  const path = raw.files?.[0]?.path ?? ''
  return {
    gid: raw.gid,
    name: basename(path) || raw.gid,
    path,
    status: raw.status,
    totalLength: toNum(raw.totalLength) || toNum(raw.files?.[0]?.length),
    completedLength: toNum(raw.completedLength),
    errorMessage: raw.errorMessage || undefined,
  }
}

/**
 * Recently stopped tasks. The backend returns the whole stopped bucket, so the
 * `limit` is applied client-side (newest-last ordering from the task queue is
 * preserved by taking the tail).
 */
export async function listStoppedTasks(limit = 5): Promise<CompletedTask[]> {
  const res = await api.get<ApiEnvelope<RawStoppedTask[]>>('/api/tasks/stopped', {
    query: { limit },
  })
  const list = unwrap<RawStoppedTask[]>(res)
  if (!Array.isArray(list)) return []
  const normalized = list.map(normalize)
  return normalized.slice(-limit).reverse()
}

/** Only successfully completed downloads (drops error/removed/paused). */
export async function listRecentCompleted(limit = 5): Promise<CompletedTask[]> {
  const res = await api.get<ApiEnvelope<RawStoppedTask[]>>('/api/tasks/stopped', {
    query: { limit: 100 },
  })
  const list = unwrap<RawStoppedTask[]>(res)
  if (!Array.isArray(list)) return []
  return list
    .filter((t) => t.status === 'complete')
    .map(normalize)
    .slice(-limit)
    .reverse()
}

/**
 * Recently **errored** downloads — task status === "error". Used by the
 * dashboard's "Recent Errors" card so operators can spot failing sources at
 * a glance and click through to the detail panel.
 */
export async function listRecentErrors(limit = 5): Promise<CompletedTask[]> {
  const res = await api.get<ApiEnvelope<RawStoppedTask[]>>('/api/tasks/stopped', {
    query: { limit: 100 },
  })
  const list = unwrap<RawStoppedTask[]>(res)
  if (!Array.isArray(list)) return []
  return list
    .filter((t) => t.status === 'error')
    .map(normalize)
    .slice(-limit)
    .reverse()
}

/**
 * Total completed count — dashboard「已完成任务」stat card. The backend has no
 * completed counter (global stat only carries numStopped), so we count the
 * stopped bucket client-side.
 */
export async function countCompletedTasks(): Promise<number> {
  const res = await api.get<ApiEnvelope<RawStoppedTask[]>>('/api/tasks/stopped', {
    query: { limit: 200 },
  })
  const list = unwrap<RawStoppedTask[]>(res)
  if (!Array.isArray(list)) return 0
  return list.filter((t) => t.status === 'complete').length
}
